import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Building2, 
  Plus, 
  MoreVertical,
  Pencil,
  Trash2,
  Users
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Departments = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchDepartments();
    fetchEmployees();
  }, []);

  const fetchDepartments = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/hr/departments`);
      setDepartments(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/hr/employees`);
      setEmployees(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSave = async () => {
    try {
      if (editingDept) {
        await axios.put(`${API_URL}/api/hr/departments/${editingDept.id}`, formData);
        toast.success(t('pages.departments.departmentUpdated'));
      } else {
        await axios.post(`${API_URL}/api/hr/departments`, formData);
        toast.success(t('pages.departments.departmentCreated'));
      }
      setShowDialog(false);
      setEditingDept(null);
      setFormData({});
      fetchDepartments();
    } catch (err) {
      toast.error(t('pages.departments.failedToSave'));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('pages.departments.confirmDelete'))) {
      try {
        await axios.delete(`${API_URL}/api/hr/departments/${id}`);
        toast.success(t('pages.departments.departmentDeleted'));
        fetchDepartments();
      } catch (err) {
        toast.error(t('pages.departments.failedToDelete'));
      }
    }
  };

  const openEdit = (dept) => {
    setEditingDept(dept);
    setFormData({ ...dept });
    setShowDialog(true);
  };

  const openNew = () => {
    setEditingDept(null);
    setFormData({});
    setShowDialog(true);
  };

  const getEmployeeCount = (deptId) => employees.filter(e => e.department_id === deptId).length;

  const colors = ['bg-blue-500', 'bg-emerald-500', 'bg-purple-500', 'bg-amber-500', 'bg-rose-500', 'bg-cyan-500'];

  return (
    <div className={`space-y-6 ${isRTL ? 'text-right' : ''}`}>
      <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('pages.departments.title')}</h1>
          <p className="text-muted-foreground">{t('pages.departments.subtitle')}</p>
        </div>
        <Button onClick={openNew} className={`gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}><Plus className="h-4 w-4" /> {t('pages.departments.addDepartment')}</Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {departments.map((dept, idx) => (
          <Card key={dept.id} className="hover:shadow-md transition-shadow">
            <CardHeader className={`flex flex-row items-center justify-between pb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`h-10 w-10 rounded-lg ${colors[idx % colors.length]} flex items-center justify-center text-white`}>
                  <Building2 className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-base">{dept.name}</CardTitle>
                  {dept.code && <p className="text-xs text-muted-foreground font-mono">{dept.code}</p>}
                </div>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="h-4 w-4" /></Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align={isRTL ? 'start' : 'end'}>
                  <DropdownMenuItem onClick={() => openEdit(dept)}><Pencil className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('pages.common.edit')}</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(dept.id)}>
                    <Trash2 className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('pages.common.delete')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </CardHeader>
            <CardContent>
              {dept.description && <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{dept.description}</p>}
              <div className={`flex items-center gap-2 text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Users className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">{getEmployeeCount(dept.id)}</span>
                <span className="text-muted-foreground">{t('pages.departments.employees')}</span>
              </div>
              <Badge variant={dept.is_active ? 'default' : 'secondary'} className="mt-3">
                {dept.is_active ? t('pages.employees.active') : t('pages.chartOfAccounts.inactive')}
              </Badge>
            </CardContent>
          </Card>
        ))}

        {departments.length === 0 && (
          <Card className="col-span-full">
            <CardContent className="py-12 text-center">
              <Building2 className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
              <p className="text-muted-foreground">{t('pages.departments.noDepartments')}</p>
              <Button onClick={openNew} variant="outline" className="mt-4">{t('pages.departments.addDepartment')}</Button>
            </CardContent>
          </Card>
        )}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingDept ? t('pages.departments.editDepartment') : t('pages.departments.addDepartment')}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('pages.departments.departmentName')} *</Label>
                <Input value={formData.name || ''} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="Engineering" />
              </div>
              <div className="space-y-2">
                <Label>{t('pages.departments.code')}</Label>
                <Input value={formData.code || ''} onChange={e => setFormData({...formData, code: e.target.value})} placeholder="ENG" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('pages.departments.description')}</Label>
              <Textarea value={formData.description || ''} onChange={e => setFormData({...formData, description: e.target.value})} rows={3} />
            </div>
          </div>
          <DialogFooter className={isRTL ? 'flex-row-reverse' : ''}>
            <Button variant="outline" onClick={() => setShowDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSave}>{editingDept ? t('pages.common.edit') : t('pages.common.add')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Departments;
